import { computed } from 'vue';
import {
  useMapGetter,
  useStore,
  useStoreGetters,
} from 'dashboard/composables/store';

// Zimobi: conversation actions are always visible at the top of the sidebar.
export const PINNED_CONVERSATION_SIDEBAR_ITEM = 'conversation_actions';

// Zimobi: desktop defaults mirror the mobile ordering. Desktop-only panels are
// appended so changing breakpoints preserves the shared visual order.
export const DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER = Object.freeze([
  { name: PINNED_CONVERSATION_SIDEBAR_ITEM },
  { name: 'macros' },
  { name: 'previous_conversation' },
  { name: 'contact_attributes' },
  { name: 'conversation_info' },
  { name: 'conversation_participants' },
  { name: 'contact_notes' },
  { name: 'linear_issues' },
  { name: 'shopify_orders' },
]);

export const DEFAULT_CONTACT_SIDEBAR_ITEMS_ORDER = Object.freeze([
  { name: 'previous_conversation' },
  { name: 'contact_attributes' },
  { name: 'contact_labels' },
]);

/**
 * Slugifies the channel name.
 * Replaces spaces, hyphens, and double colons with underscores.
 * @param {string} name - The channel name to slugify.
 * @returns {string} The slugified channel name.
 */
const slugifyChannel = name =>
  name?.toLowerCase().replace(' ', '_').replace('-', '_').replace('::', '_');

/**
 * Computes the order of items in the conversation sidebar, using defaults if not present.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {Array} Ordered list of sidebar items.
 */
// Zimobi: complete legacy orders and move the pinned action back to the top.
// This also repairs orders saved before pinning was introduced.
export const normalizeConversationSidebarItems = items => {
  const itemsOrder = [...(items || [])];

  DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER.forEach(item => {
    if (!itemsOrder.some(currentItem => currentItem.name === item.name)) {
      itemsOrder.push(item);
    }
  });

  const pinnedItem = itemsOrder.find(
    item => item.name === PINNED_CONVERSATION_SIDEBAR_ITEM
  );

  return [
    pinnedItem,
    ...itemsOrder.filter(
      item => item.name !== PINNED_CONVERSATION_SIDEBAR_ITEM
    ),
  ];
};

const useConversationSidebarItemsOrder = uiSettings =>
  computed(() =>
    normalizeConversationSidebarItems(
      uiSettings.value.conversation_sidebar_items_order
    )
  );

// Zimobi: use vuedraggable's actual event payload. The pinned item cannot be
// dragged and no other item may be dropped into its index.
export const canMoveConversationSidebarItem = event => {
  const { element, futureIndex } = event?.draggedContext || {};

  return (
    element?.name !== PINNED_CONVERSATION_SIDEBAR_ITEM &&
    Number.isInteger(futureIndex) &&
    futureIndex > 0
  );
};

/**
 * Computes the order of items in the contact sidebar,using defaults if not present.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {Array} Ordered list of sidebar items.
 */
const useContactSidebarItemsOrder = uiSettings => {
  return computed(() => {
    const { contact_sidebar_items_order: itemsOrder } = uiSettings.value;
    return itemsOrder || DEFAULT_CONTACT_SIDEBAR_ITEMS_ORDER;
  });
};

/**
 * Toggles the open state of a sidebar item.
 * @param {string} key - The key of the sidebar item to toggle.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @param {Function} updateUISettings - Function to update UI settings.
 */
const toggleSidebarUIState = (key, uiSettings, updateUISettings) => {
  updateUISettings({ [key]: !uiSettings.value[key] });
};

/**
 * Sets the signature flag for a specific channel type in the inbox settings.
 * @param {string} channelType - The type of the channel.
 * @param {boolean} value - The value to set for the signature enabled flag.
 * @param {Function} updateUISettings - Function to update UI settings.
 */
const setSignatureFlagForInbox = (channelType, value, updateUISettings) => {
  if (!channelType) return;

  const slugifiedChannel = slugifyChannel(channelType);
  updateUISettings({ [`${slugifiedChannel}_signature_enabled`]: value });
};

const setQuotedReplyFlagForInbox = (channelType, value, updateUISettings) => {
  if (!channelType) return;

  const slugifiedChannel = slugifyChannel(channelType);
  updateUISettings({ [`${slugifiedChannel}_quoted_reply_enabled`]: value });
};

/**
 * Fetches the signature flag for a specific channel type from UI settings.
 * @param {string} channelType - The type of the channel.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {boolean} The value of the signature enabled flag.
 */
const fetchSignatureFlagFromUISettings = (channelType, uiSettings) => {
  if (!channelType) return false;

  const slugifiedChannel = slugifyChannel(channelType);
  return uiSettings.value[`${slugifiedChannel}_signature_enabled`];
};

const fetchQuotedReplyFlagFromUISettings = (channelType, uiSettings) => {
  if (!channelType) return false;

  const slugifiedChannel = slugifyChannel(channelType);
  return uiSettings.value[`${slugifiedChannel}_quoted_reply_enabled`];
};

/**
 * Checks if a specific editor hotkey is enabled.
 * @param {string} key - The key to check.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {boolean} True if the hotkey is enabled, otherwise false.
 */
const isEditorHotKeyEnabled = (key, uiSettings) => {
  const {
    editor_message_key: editorMessageKey,
    enter_to_send_enabled: enterToSendEnabled,
  } = uiSettings.value || {};
  if (!editorMessageKey) {
    return key === (enterToSendEnabled ? 'enter' : 'cmd_enter');
  }
  return editorMessageKey === key;
};

// Zimobi: this sidebar needs a reactive settings source because it remains
// mounted while the viewport crosses the mobile breakpoint. Reading the
// original getUISettings getter only once could merge mobile state into a
// desktop update (or the reverse).
export function useConversationSidebarSettings(isAtMobile) {
  const store = useStore();
  const currentUser = useMapGetter('getCurrentUser');

  const uiSettings = computed(() => {
    const user = currentUser.value || {};

    return isAtMobile.value
      ? user.mobile_ui_settings || {}
      : user.ui_settings || {};
  });

  const updateUISettings = (settings = {}) =>
    store.dispatch('updateUISettings', {
      uiSettings: {
        ...uiSettings.value,
        ...settings,
      },
    });

  return {
    uiSettings,
    updateUISettings,
    conversationSidebarItemsOrder: useConversationSidebarItemsOrder(uiSettings),
  };
}

/**
 * Main composable function for managing UI settings.
 * @returns {Object} An object containing reactive properties and methods for UI settings management.
 */
export function useUISettings() {
  const getters = useStoreGetters();
  const store = useStore();
  const uiSettings = computed(() => getters.getUISettings.value);

  const updateUISettings = (settings = {}) => {
    store.dispatch('updateUISettings', {
      uiSettings: {
        ...uiSettings.value,
        ...settings,
      },
    });
  };

  return {
    uiSettings,
    updateUISettings,
    conversationSidebarItemsOrder: useConversationSidebarItemsOrder(uiSettings),
    contactSidebarItemsOrder: useContactSidebarItemsOrder(uiSettings),
    isContactSidebarItemOpen: key => !!uiSettings.value[key],
    toggleSidebarUIState: key =>
      toggleSidebarUIState(key, uiSettings, updateUISettings),
    setSignatureFlagForInbox: (channelType, value) =>
      setSignatureFlagForInbox(channelType, value, updateUISettings),
    fetchSignatureFlagFromUISettings: channelType =>
      fetchSignatureFlagFromUISettings(channelType, uiSettings),
    setQuotedReplyFlagForInbox: (channelType, value) =>
      setQuotedReplyFlagForInbox(channelType, value, updateUISettings),
    fetchQuotedReplyFlagFromUISettings: channelType =>
      fetchQuotedReplyFlagFromUISettings(channelType, uiSettings),
    isEditorHotKeyEnabled: key => isEditorHotKeyEnabled(key, uiSettings),
  };
}
